import type { FC } from 'react';

import { tr } from './translations';
import { VideoCardShell } from './VideoCardShell';

// Inline, not `aspect-video`: Orchid's Tailwind theme replaces aspectRatio, so the
// utility is not generated there and absolutely positioned content collapses to 0.
const VIDEO_ASPECT = { aspectRatio: '16 / 9' };

export interface VideoStatusCardProps {
  status: 'processing' | 'failed' | 'blocked';
  videoId?: string;
  poster?: string;
  // The failed card shows a retry button only when this is set.
  onRetry?: () => void;
  retrying?: boolean;
  // Replaces the generic failed or blocked message, such as with why the video failed.
  message?: string;
  // A second line under a failed or blocked status, such as what the author can do next.
  hint?: string;
  translations?: Record<string, string>;
}

export const VideoStatusCard: FC<VideoStatusCardProps> = ({
  status,
  videoId,
  poster,
  onRetry,
  retrying,
  message,
  hint,
  translations,
}) => {
  if (status === 'processing') {
    return (
      <div
        className="relative w-full overflow-hidden rounded-xl bg-black"
        style={VIDEO_ASPECT}
        data-video-id={videoId}
        data-video-status={status}
      >
        {poster && <img src={poster} alt="" className="absolute inset-0 size-full object-cover" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 text-white">
          <span
            className="size-5 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
          <span className="text-sm">{tr(translations, 'video.status_processing')}</span>
        </div>
      </div>
    );
  }

  return (
    <VideoCardShell status={status} videoId={videoId}>
      <div className="flex flex-col items-center gap-0.5 text-center text-red-500">
        <span className="text-sm">
          {message ?? tr(translations, status === 'blocked' ? 'video.status_blocked' : 'video.status_failed')}
        </span>
        {hint && <span className="text-xs">{hint}</span>}
      </div>
      {status === 'failed' && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="rounded-full bg-black/70 px-3 py-1 text-xs text-white transition-colors hover:bg-black disabled:opacity-50"
        >
          {tr(translations, 'video.retry')}
        </button>
      )}
    </VideoCardShell>
  );
};
